// src/hooks/useElementSize.ts
import { useState, RefObject, useLayoutEffect } from 'react'

import { useIsMobile } from './useIsMobile'

interface Size {
  width: number
  height: number
}

export function useElementSize<T extends HTMLElement>(ref: RefObject<T>): Size {
  const isMobile = useIsMobile('sm')

  const [size, setSize] = useState<Size>({ width: 0, height: 0 })

  useLayoutEffect(() => {
    if (!ref.current) return
    // initialize
    setSize({
      width: ref.current.offsetWidth,
      height: ref.current.offsetHeight,
    })
    // watch for resizes
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]

      const borderBoxSize = entry.borderBoxSize?.[0] || entry.borderBoxSize
      if (borderBoxSize) {
        setSize({ width: borderBoxSize.inlineSize, height: borderBoxSize.blockSize })
      } else {
        const rect = entry.contentRect
        setSize({ width: rect.width, height: rect.height })
      }
    })
    observer.observe(ref.current)
    return () => {
      observer.disconnect()
    }
  }, [ref, isMobile])

  return size
}
